import Button from "@/components/ui/button";
import CustomInput from "@/components/ui/input";
import { authApi } from "@/core/auth/services/auth.api";
import { setUserFromToken } from "@/store";
import { t } from "i18next";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../styles/login.module.scss";
import Email from "@/components/icons/email";
import { Lock } from "@/components/icons";
import useNotification from "@/hooks/use-notification";
import LoginCaptcha from "./LoginCaptcha";
import { useLoginCaptcha } from "../hooks/useLoginCaptcha";
import { resolveLoginError } from "../utils/loginError.utils";

interface AdminLoginProps {
  currentDomainID: string;
  currentDomainName: string;
  /**
   * Varsa parent user type doğrulaması yapacak - setUserFromToken/navigate parent'ta.
   * `false` dönerse giriş reddedilmiş sayılır ve yanlış şifreyle birebir aynı
   * genel hata gösterilir (pentest 3.1.1 — hesap varlığı sızdırılmaz).
   */
  onLoginSuccess?: (response: any) => boolean | void;
}

type FieldErrors = Partial<Record<"username" | "password", string>>;

const AdminLogin: React.FC<AdminLoginProps> = ({
  currentDomainID,
  currentDomainName,
  onLoginSuccess,
}) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    password: "",
    username: "",
  });
  const { notifyError, notifySuccess } = useNotification();
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const [, setDomainID] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const {
    captchaRequired,
    captchaPayload,
    captchaKey,
    setCaptchaPayload,
    resetAfterAttempt,
    isCaptchaBlocking,
  } = useLoginCaptcha();

  useEffect(() => {
    if (currentDomainID) {
      setDomainID(currentDomainID);
    }
  }, [currentDomainID, currentDomainName]);

  const formFields = [
    {
      label: t("login.username"),
      name: "username",
      placeholder: t("login.usernamePlaceholder"),
      type: "text",
      maxLength: 255,
      variant: "standard" as const,
      startIcon: <Email width={24} height={24} />,
    },
    {
      label: t("login.password"),
      name: "password",
      placeholder: "**********",
      type: "password",
      maxLength: 512,
      variant: "standard" as const,
      startIcon: <Lock width={24} height={24} />,
    },
  ];

  const handleInputChange =
    (field: "username" | "password") =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
      setForm((prevForm) => ({
        ...prevForm,
        [field]: e.target.value,
      }));
    };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const usernameEmpty = !form.username.trim();
    const passwordEmpty = !form.password.trim();
    if (usernameEmpty || passwordEmpty) {
      const errors: FieldErrors = {};
      if (usernameEmpty) errors.username = t("login.pleaseEnterUsername");
      if (passwordEmpty) errors.password = t("login.pleaseEnterPassword");
      setFieldErrors(errors);
      const firstMessage = errors.username || errors.password;
      if (firstMessage) notifyError(firstMessage);
      return;
    }

    if (isCaptchaBlocking) {
      notifyError(t("login.captcha.verificationRequired"));
      return;
    }

    setFieldErrors({});
    setIsLoading(true);

    const markInvalidCredentials = () => {
      setFieldErrors({
        username: t("login.invalidUsername"),
        password: t("login.invalidPassword"),
      });
    };

    try {
      const response = await authApi.login({
        username: form.username.trim(),
        password: form.password,
        portal: "ADMIN",
        captcha: captchaPayload ?? undefined,
      });
      resetAfterAttempt();

      if (onLoginSuccess) {
        if (onLoginSuccess(response) === false) {
          markInvalidCredentials();
          notifyError(t("login.invalidCredentials"));
        }
        return;
      }

      setUserFromToken(response, "admin");
      await new Promise((resolve) => setTimeout(resolve, 100));
      notifySuccess(t("login.loginSuccess"));
      navigate("/dashboard");
    } catch (err: any) {
      // 401'de backend mesajı yok sayılır; her durumda aynı genel metin.
      const { message, markFields, captchaRequired: required } =
        resolveLoginError(err);
      resetAfterAttempt(required);
      if (markFields) markInvalidCredentials();
      notifyError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={styles["user-login"]}>
      {formFields.map(
        ({ label, name, placeholder, type, variant, startIcon, maxLength }) => {
          const errorMessage = fieldErrors[name as keyof FieldErrors];
          return (
            <CustomInput
              key={name}
              error={!!errorMessage}
              errorMessage={errorMessage}
              label={label}
              onChange={handleInputChange(name as "username" | "password")}
              placeholder={placeholder}
              type={type}
              maxLength={maxLength}
              value={form[name as keyof typeof form]}
              variant={variant}
              startIcon={startIcon}
            />
          );
        },
      )}

      {/* Sabit, hesaptan bağımsız yönlendirme — hiçbir hesap bilgisi sızdırmaz. */}
      <p className={styles["admin-portal-hint"]}>{t("login.adminPortalHint")}</p>

      {captchaRequired && (
        <LoginCaptcha key={captchaKey} onChange={setCaptchaPayload} />
      )}

      <Button
        color="primary"
        type="submit"
        variant="contained"
        loading={isLoading}
        disabled={isCaptchaBlocking}
      >
        {t("buttons.login")}
      </Button>
    </form>
  );
};

export default AdminLogin;
